import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import { getProductImages } from "@/lib/productImages";
import { ProductImage, ProductCard, formatPrice } from "@/components/ProductCard";
import { uploadedImageUrl } from "@/components/ImageUploader";
import { RichText } from "@/components/RichText";
import { EnquiryDialog } from "@/components/EnquiryDialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Ruler, TrendingUp, Check, Loader2 } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [enq, setEnq] = useState({ open: false, product: null });
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setActiveImg(0);
    api.get(`/products/${slug}`)
      .then((res) => setProduct(res.data))
      .catch(async () => {
        const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
        const target = norm(slug);
        const all = await api.get("/products").then((r) => r.data).catch(() => []);
        const match = all.find((p) => norm(p.slug) === target || norm(p.name) === target);
        if (match && match.slug !== slug) return navigate(`/product/${match.slug}`, { replace: true });
        const s = slug.toLowerCase();
        const dest = /line|paint|marker|spectro|baufan/.test(s) ? "/line-marking"
          : /fjd|titan|frx|commercial/.test(s) ? "/commercial" : "/domestic";
        navigate(dest, { replace: true });
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand-navy" /></div>;

  if (notFound || !product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display font-bold text-3xl text-brand-ink">Product not found</h1>
        <Button asChild className="mt-6 bg-brand-navy text-white"><Link to="/domestic">Back to catalogue</Link></Button>
      </div>
    );
  }

  const isDomestic = product.category === "domestic";
  const enquiryType = isDomestic ? "domestic" : product.category === "line_marking" ? "line_marking" : "commercial";
  const fallback = product.category === "line_marking" ? IMAGES.lineMarking : IMAGES.commercial;
  const uploaded = (product.uploaded_images || []).map(uploadedImageUrl);
  const curated = uploaded.length ? uploaded : getProductImages(slug);
  const galleryImages = curated.length
    ? curated
    : (product.gallery && product.gallery.length ? product.gallery : (isDomestic ? (product.image_url ? [product.image_url] : []) : [fallback]));
  const hasRealPhotos = curated.length > 0 || (product.gallery && product.gallery.length > 0) || (isDomestic && !!product.image_url);
  const activeSrc = galleryImages[activeImg] || galleryImages[0];
  const price = formatPrice(product.price);

  const openEnquiry = () => setEnq({ open: true, product: { id: product.id, name: product.name } });

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-brand-navy hover:text-brand-navy transition-colors" data-testid="product-back-button">
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <div className="rounded-3xl border border-brand-navy/10 bg-brand-white overflow-hidden">
            {isDomestic ? (
              <ProductImage src={activeSrc} alt={product.name} className="w-full h-[24rem] p-8" />
            ) : (
              <img src={activeSrc} alt={product.name} className="w-full h-[24rem] object-cover" data-testid="product-main-image" />
            )}
          </div>
          {galleryImages.length > 1 && (
            <div className="mt-4 grid grid-cols-4 gap-3" data-testid="product-thumbnails">
              {galleryImages.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`rounded-xl overflow-hidden border-2 transition-colors ${i === activeImg ? "border-brand-navy" : "border-brand-navy/10 hover:border-brand-navy/40"}`}
                  data-testid={`product-thumb-${i}`}
                >
                  <img src={src} alt={`${product.name} view ${i + 1}`} className={`w-full h-20 ${isDomestic ? "object-contain bg-white p-1" : "object-cover"}`} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          {product.brand && <span className="font-mono-spec text-xs uppercase tracking-widest text-brand-navy">{product.brand}</span>}
          <h1 className="mt-2 font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-brand-ink" data-testid="product-title">{product.name}</h1>
          <RichText html={product.positioning} testId="product-positioning" className="mt-4 text-lg text-muted-foreground" />

          <div className="mt-6 flex flex-wrap gap-2">
            {product.area_m2 != null && <span className="inline-flex items-center gap-1 text-sm font-mono-spec px-3 py-1.5 rounded-md bg-brand-navy/10 text-brand-navy"><Ruler className="h-4 w-4" /> {product.area_m2.toLocaleString("en-IE")} m²</span>}
            {product.max_slope != null && <span className="inline-flex items-center gap-1 text-sm font-mono-spec px-3 py-1.5 rounded-md bg-brand-navy/10 text-brand-navy"><TrendingUp className="h-4 w-4" /> {product.max_slope}% slope</span>}
          </div>

          {product.highlights?.length > 0 && (
            <ul className="mt-6 space-y-2">
              {product.highlights.map((h) => <li key={h} className="flex items-center gap-2 text-brand-ink"><Check className="h-4 w-4 text-brand-navy" /> {h}</li>)}
            </ul>
          )}

          <div className="mt-8 rounded-2xl border border-brand-navy/10 bg-white p-6 flex flex-wrap items-center justify-between gap-4">
            {price ? (
              <div><span className="text-xs font-mono-spec uppercase tracking-widest text-muted-foreground">Price</span><div className="font-display font-bold text-3xl text-brand-navy" data-testid="product-detail-price">{price}</div></div>
            ) : (
              <div><span className="text-xs font-mono-spec uppercase tracking-widest text-muted-foreground">Pricing</span><div className="font-display font-semibold text-xl text-brand-ink">Enquiry-led</div></div>
            )}
            <Button onClick={openEnquiry} size="lg" className="bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold" data-testid="product-detail-enquire">Enquire about this product</Button>
          </div>
        </div>
      </section>

      {Object.keys(product.specs || {}).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h2 className="font-display font-bold text-2xl text-brand-ink">Specifications</h2>
          <dl className="mt-4 rounded-2xl border border-brand-navy/10 bg-white divide-y divide-brand-navy/10">
            {Object.entries(product.specs).map(([k, v]) => (
              <div key={k} className="flex justify-between px-6 py-3"><dt className="text-muted-foreground">{k}</dt><dd className="font-medium text-brand-ink font-mono-spec">{v}</dd></div>
            ))}
          </dl>
        </section>
      )}

      {product.support_content && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="rounded-2xl bg-brand-white border border-brand-navy/10 p-6">
            <h2 className="font-display font-bold text-xl text-brand-ink">Support & guidance</h2>
            <RichText html={product.support_content} testId="product-support-content" className="mt-2 text-muted-foreground" />
          </div>
        </section>
      )}

      {product.related_products?.length > 0 && (
        <section className="bg-white border-t border-brand-navy/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="font-display font-bold text-2xl text-brand-ink">Related products</h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {product.related_products.map((p) => <ProductCard key={p.slug} product={p} onEnquire={(prod) => setEnq({ open: true, product: { id: prod.id, name: prod.name } })} />)}
            </div>
          </div>
        </section>
      )}

      <EnquiryDialog open={enq.open} onOpenChange={(o) => setEnq((e) => ({ ...e, open: o }))} type={enquiryType} product={enq.product} sourcePage={`product/${slug}`} />
    </div>
  );
}
